/**
 * components/wishlist/done-animation.tsx
 *
 * "Flies into jar" overlay animation — 4 phases, 900ms total.
 * Phase 1 (0–200ms):   item row scales down + lifts up
 * Phase 2 (200–500ms): item flies upward and right toward header jar icon
 * Phase 3 (500–700ms): jar bounces on receive
 * Phase 4 (700–900ms): confetti burst in vibe accent color, item row fades out
 *
 * Usage:
 *   <DoneAnimation vibe={item.vibe} onComplete={() => ...} />
 */
import { useEffect, useState } from 'react'
import type { WishlistVibe } from '@just-us/shared'
import { VIBE_META } from './vibe-chip'

interface DoneAnimationProps {
  vibe: WishlistVibe
  onComplete: () => void
}

export default function DoneAnimation({ vibe, onComplete }: DoneAnimationProps) {
  const meta = VIBE_META[vibe]
  const [phase, setPhase] = useState(1)

  // Trigger haptic if available
  useEffect(() => {
    if ('vibrate' in navigator) navigator.vibrate([30, 60, 100])
  }, [])

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(2), 200)
    const t2 = setTimeout(() => setPhase(3), 500)
    const t3 = setTimeout(() => setPhase(4), 700)
    const t4 = setTimeout(() => onComplete(), 900)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4) }
  }, [onComplete])

  return (
    <div className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
      {/* Flying dot */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: meta.accent,
          transform: 'translate(-50%, -50%)',
          transition: 'all 700ms cubic-bezier(0.4,0,0.2,1)',
          opacity: phase >= 3 ? 0 : 1,
          ...(phase >= 2 && {
            transform: 'translate(calc(-50% + 80px), calc(-50% - 200px)) scale(0.3)',
          }),
        }}
      />

      {/* Confetti burst */}
      {phase >= 4 && (
        <div
          className="absolute left-1/2 top-[15%]"
          style={{ transform: 'translate(-50%, -50%)' }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-[8px] h-[8px] rounded-full animate-confetti-burst"
              style={{
                background: meta.accent,
                transform: `rotate(${i * 45}deg) translateY(-24px)`,
                animationDelay: `${i * 30}ms`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
